import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  Download,
  Loader2,
  Phone,
  Mail,
  MapPin,
  Trash2,
  RefreshCw,
  Inbox,
  PhoneCall,
  CheckCircle2,
  Archive,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { adminApi, getToken } from "./api";
import { useToast } from "../hooks/use-toast";

const STATUS_META = {
  new: { label: "New", className: "bg-orange-100 text-orange-700 border-orange-200", icon: Inbox },
  contacted: { label: "Contacted", className: "bg-blue-100 text-blue-700 border-blue-200", icon: PhoneCall },
  converted: { label: "Converted", className: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  archived: { label: "Archived", className: "bg-slate-200 text-slate-700 border-slate-300", icon: Archive },
};

export default function EnquiriesPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ count: 0, byStatus: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [editStatus, setEditStatus] = useState("new");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const [list, c] = await Promise.all([
        adminApi.listEnquiries(params),
        adminApi.countEnquiries(),
      ]);
      setItems(list.data);
      setCounts(c.data);
    } catch (e) {
      toast({ title: "Failed to load", description: e?.message || "Try again" });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const openEdit = (it) => {
    setEditing(it);
    setEditStatus(it.status || "new");
    setEditNotes(it.notes || "");
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { data } = await adminApi.updateEnquiry(editing.id, {
        status: editStatus,
        notes: editNotes,
      });
      setItems((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      setEditing(null);
      toast({ title: "Enquiry updated" });
      // refresh counts
      const c = await adminApi.countEnquiries();
      setCounts(c.data);
    } catch (e) {
      toast({ title: "Update failed", description: e?.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await adminApi.deleteEnquiry(id);
      setItems((prev) => prev.filter((p) => p.id !== id));
      const c = await adminApi.countEnquiries();
      setCounts(c.data);
      toast({ title: "Deleted" });
    } catch (e) {
      toast({ title: "Delete failed", description: e?.message });
    }
  };

  const exportCsv = async () => {
    try {
      const url = adminApi.exportUrl();
      const token = getToken();
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `zenova-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast({ title: "Export failed", description: e?.message });
    }
  };

  const statusCards = useMemo(
    () => [
      { key: "all", label: "Total", value: counts.count || 0, color: "#0a4f47" },
      { key: "new", label: "New", value: counts.byStatus?.new || 0, color: "#f97316" },
      { key: "contacted", label: "Contacted", value: counts.byStatus?.contacted || 0, color: "#2563eb" },
      { key: "converted", label: "Converted", value: counts.byStatus?.converted || 0, color: "#10b981" },
      { key: "archived", label: "Archived", value: counts.byStatus?.archived || 0, color: "#64748b" },
    ],
    [counts]
  );

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-900">Enquiries</h1>
          <p className="text-slate-500 text-sm">Manage leads from your website&apos;s Contact Us form.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" className="gap-1.5 h-10"><RefreshCw className="w-4 h-4" /> Refresh</Button>
          <Button onClick={exportCsv} className="bg-[#0a4f47] hover:bg-[#0e6e63] text-white gap-1.5 h-10"><Download className="w-4 h-4" /> Export CSV</Button>
        </div>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {statusCards.map((c) => (
          <button
            key={c.key}
            onClick={() => setStatusFilter(c.key)}
            className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${
              statusFilter === c.key ? "border-[#0a4f47] bg-emerald-50/40 shadow-sm" : "border-slate-200 bg-white"
            }`}
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className="text-3xl font-extrabold mt-1" style={{ color: c.color }}>{c.value}</div>
          </button>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={onSearch} className="flex gap-2 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, phone, email or state" className="h-11 pl-10" />
        </div>
        <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white h-11 px-5">Search</Button>
      </form>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <Inbox className="w-10 h-10 mx-auto text-slate-300" />
            <div className="mt-3 font-medium">No enquiries match the current filter.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">State / Bill</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Received</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const meta = STATUS_META[it.status] || STATUS_META.new;
                  const Icon = meta.icon;
                  return (
                    <tr key={it.id} className="border-b border-slate-100 hover:bg-orange-50/30">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{it.firstName} {it.lastName}</div>
                        <div className="text-xs text-slate-500">{it.source}</div>
                      </td>
                      <td className="px-4 py-3">
                        <a href={`tel:${it.phone}`} className="flex items-center gap-1.5 text-slate-700 hover:text-[#0a4f47]"><Phone className="w-3.5 h-3.5" /> {it.phone}</a>
                        {it.email ? (
                          <a href={`mailto:${it.email}`} className="flex items-center gap-1.5 text-slate-500 text-xs mt-0.5 hover:text-[#0a4f47]"><Mail className="w-3 h-3" /> {it.email}</a>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-slate-700"><MapPin className="w-3.5 h-3.5" /> {it.state}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{it.bill || "—"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`gap-1 ${meta.className}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {new Date(it.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="outline" className="h-8" onClick={() => openEdit(it)}>Update</Button>
                          <Button size="sm" variant="ghost" className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => remove(it.id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update enquiry status</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                <div className="font-semibold text-slate-900">{editing.firstName} {editing.lastName}</div>
                <div className="text-slate-600">+91 {editing.phone} • {editing.state}</div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Status</label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Internal notes</label>
                <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={4} className="mt-1.5" placeholder="Call notes, follow-up reminders, quote shared etc." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button disabled={saving} onClick={saveEdit} className="bg-[#0a4f47] hover:bg-[#0e6e63] text-white">
              {saving ? (<span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Saving</span>) : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
